import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  addDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  where,
  arrayUnion,
  DocumentData,
} from 'firebase/firestore';
import { db } from '../firebase';
import { getHistoryFloor } from '../utils/timeRange';
import { ChurchEvent } from '../types';
import { useAuth } from './AuthContext';
import { assertUserHasRole, getAuthenticatedUserId } from '../utils/roleAuth';
import { requestNewEventEmail } from '../utils/appEmails';

interface EventsContextType {
  events: ChurchEvent[];
  loading: boolean;
  /**
   * Whether `readEventIds` has resolved at least once. It loads from a second, independent
   * listener (`eventReadState/{userId}`), so `events` can arrive before it does — and until it
   * does, every event looks unread. Consumers that turn `unreadEvents` into a count (the
   * notification bell) should wait for this too, not just `loading`, or they flash an inflated
   * count before settling on the real one.
   */
  readStateLoaded: boolean;
  unreadEvents: ChurchEvent[];
  unreadEventCount: number;
  canManageEvents: boolean;
  createEvent: (data: Omit<ChurchEvent, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>) => Promise<void>;
  updateEvent: (id: string, data: Omit<ChurchEvent, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>) => Promise<void>;
  deleteEvent: (id: string) => Promise<void>;
  markEventsAsRead: (eventIds: string[]) => Promise<void>;
}

const EventsContext = createContext<EventsContextType>({} as EventsContextType);

export const useEvents = () => {
  const context = useContext(EventsContext);
  if (!context) {
    throw new Error('useEvents must be used within an EventsProvider');
  }
  return context;
};

function firestoreToEvent(id: string, data: DocumentData): ChurchEvent {
  return {
    id,
    name: data.name,
    description: data.description,
    startDate: data.startDate.toDate(),
    endDate: data.endDate.toDate(),
    startTime: data.startTime,
    endTime: data.endTime,
    registrationUrl: data.registrationUrl || undefined,
    createdBy: data.createdBy,
    createdAt: data.createdAt.toDate(),
    updatedAt: data.updatedAt.toDate(),
  };
}

export const EventsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const [events, setEvents] = useState<ChurchEvent[]>([]);
  const [readEventIds, setReadEventIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [readStateLoaded, setReadStateLoaded] = useState(false);

  const canManageEvents =
    currentUser?.role === 'admin' || currentUser?.role === 'leader';

  useEffect(() => {
    if (!currentUser?.id) {
      setEvents([]);
      setReadEventIds(new Set());
      setLoading(false);
      return;
    }

    setLoading(true);

    // Same window as the calendar: the last three months plus everything ahead. Inequality
    // and orderBy on one field, so the automatic single-field index serves it, and the listener
    // stays live — a new event still reaches every open client at once.
    const eventsQuery = query(
      collection(db, 'events'),
      where('startDate', '>=', getHistoryFloor()),
      orderBy('startDate', 'desc')
    );
    const unsubscribeEvents = onSnapshot(
      eventsQuery,
      (snapshot) => {
        const items: ChurchEvent[] = [];
        snapshot.forEach((docSnap) => {
          items.push(firestoreToEvent(docSnap.id, docSnap.data()));
        });
        setEvents(items);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading events:', error);
        setLoading(false);
      }
    );

    return unsubscribeEvents;
    // `currentUser?.id`, not the whole object: `persistUser` hands out a new object on
    // every write (role refresh, tutorial flag, the user doc's own live listener bouncing
    // back), which used to resubscribe this listener — and flip `loading` true then false
    // again — on each one, for no change in who's signed in.
  }, [currentUser?.id]);

  useEffect(() => {
    const userId = currentUser?.id;
    if (!userId) {
      setReadEventIds(new Set());
      // No user means no read state to wait for — trivially resolved rather than pending.
      setReadStateLoaded(true);
      return;
    }

    setReadStateLoaded(false);
    const unsubscribe = onSnapshot(doc(db, 'eventReadState', userId), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setReadEventIds(new Set(data.readEventIds || []));
      } else {
        setReadEventIds(new Set());
      }
      setReadStateLoaded(true);
    });

    return unsubscribe;
  }, [currentUser?.id]);

  const unreadEvents = useMemo(
    () => events.filter((event) => !readEventIds.has(event.id)),
    [events, readEventIds]
  );

  const markEventsAsRead = useCallback(
    async (eventIds: string[]) => {
      const userId = currentUser?.id;
      if (!userId || eventIds.length === 0) return;

      setReadEventIds((prev) => new Set([...Array.from(prev), ...eventIds]));

      try {
        // Merged write, so no read to decide between create and update and no race between
        // concurrent callers. `createdAt` is dropped: a merged write would reset it each time.
        await setDoc(
          doc(db, 'eventReadState', userId),
          { userId, readEventIds: arrayUnion(...eventIds), updatedAt: new Date() },
          { merge: true }
        );
      } catch (error) {
        console.error('Error marking events as read:', error);
      }
    },
    [currentUser?.id]
  );

  const createEvent = useCallback(
    async (data: Omit<ChurchEvent, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>) => {
      const userId = getAuthenticatedUserId();
      await assertUserHasRole(userId, ['admin', 'leader']);

      const docRef = await addDoc(collection(db, 'events'), {
        name: data.name,
        description: data.description,
        startDate: data.startDate,
        endDate: data.endDate,
        startTime: data.startTime,
        endTime: data.endTime,
        registrationUrl: data.registrationUrl ?? null,
        createdBy: userId,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      requestNewEventEmail(docRef.id);
    },
    []
  );

  const updateEvent = useCallback(
    async (
      id: string,
      data: Omit<ChurchEvent, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>
    ) => {
      const userId = getAuthenticatedUserId();
      await assertUserHasRole(userId, ['admin', 'leader']);

      await updateDoc(doc(db, 'events', id), {
        name: data.name,
        description: data.description,
        startDate: data.startDate,
        endDate: data.endDate,
        startTime: data.startTime,
        endTime: data.endTime,
        registrationUrl: data.registrationUrl ?? null,
        updatedAt: new Date(),
      });
    },
    []
  );

  const deleteEvent = useCallback(async (id: string) => {
    const userId = getAuthenticatedUserId();
    await assertUserHasRole(userId, ['admin', 'leader']);
    await deleteDoc(doc(db, 'events', id));
  }, []);

  const value: EventsContextType = {
    events,
    loading,
    readStateLoaded,
    unreadEvents,
    unreadEventCount: unreadEvents.length,
    canManageEvents,
    createEvent,
    updateEvent,
    deleteEvent,
    markEventsAsRead,
  };

  return <EventsContext.Provider value={value}>{children}</EventsContext.Provider>;
};
